import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import he from "he";
import "./editor.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import {
  deleteQuestion,
  exportQuestionsCSV,
  getAdminQuestions,
  importQuestionsCSV,
  saveQuestion,
} from "../../functions/getAdminQuestions";
import { GuidedTourOverlay, useGuidedTour } from "../../components/GuidedTour";

// Set by rules.jsx when its own tour hands off here.
const TOUR_HANDOFF_KEY = "adminRulesEditorTour_handoff";

const TOUR_STEPS = [
  {
    selector: ".qe-actions",
    title: "Build Your Question Bank",
    description:
      "Add a question one at a time, bulk-import many at once via CSV, or export/download what you already have — including a sample CSV to see the required format.",
  },
  {
    selector: ".qe-card",
    title: "Your Questions",
    description: "Every question you've added shows up here — use Edit or Delete on any row to manage it.",
  },
  {
    selector: ".qe-pagination",
    title: "Paging",
    description: "More than 10 questions? Page through them here.",
  },
];

const MAX_QUESTION_LEN = 350;
const MAX_ANSWER_LEN = 50;
const MEDIA_ACCEPT = ".png,.jpg,.jpeg,.gif";
const ALLOWED_EXT = ["jpg", "jpeg", "png", "gif"];
const MAX_FILE_MB = 10;
const PAGE_SIZE = 10;

/** Page numbers with "..." gaps, e.g. 1 ... 4 5 6 ... 12 */
function pageItems(page, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(total - 1, page + 1);
  if (start > 2) items.push("...");
  for (let i = start; i <= end; i++) items.push(i);
  if (end < total - 1) items.push("...");
  items.push(total);
  return items;
}

function Media({ q }) {
  if (!q.hintUrl) return null;
  if (q.hintType === "video") {
    return (
      <video className="qe-media" controls>
        <source src={q.hintUrl} type="video/mp4" />
      </video>
    );
  }
  if (q.hintType === "audio") {
    return (
      <audio className="qe-media" controls>
        <source src={q.hintUrl} type="audio/mpeg" />
      </audio>
    );
  }
  return <img className="qe-media" src={q.hintUrl} alt="" />;
}

export default function Editor() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // add / edit modal
  const [modal, setModal] = useState(null); // null | { mode: 'add' | 'edit', id }
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // csv modal
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);

  const { tourRunning, tourStep, tourRect, startTour, endTour, setTourStep } = useGuidedTour({ steps: TOUR_STEPS });
  const handleTourNext = () => {
    if (tourStep >= TOUR_STEPS.length - 1) {
      endTour();
      return;
    }
    setTourStep((i) => i + 1);
  };

  // Pick up where the rules-page tour left off, if that's how we got here.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(TOUR_HANDOFF_KEY)) {
        sessionStorage.removeItem(TOUR_HANDOFF_KEY);
        const t = setTimeout(() => startTour(), 500);
        return () => clearTimeout(t);
      }
    } catch (_) {
      /* ignore */
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setLoading(true);
    try {
      const json = await getAdminQuestions({ token: adminToken, themeName: currentTheme });
      setQuestions(json.questions || []);
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [currentTheme, adminToken]);

  useEffect(() => {
    if (currentTheme && !data) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme, data]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(questions.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const visible = questions.slice(pageStart, pageStart + PAGE_SIZE);

  const onPickFile = (e) => {
    const f = e.target.files[0] || null;
    if (f) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        Swal.fire("Invalid file", "Allowed formats: JPG, JPEG, PNG, GIF only.", "error");
        e.target.value = "";
        setFile(null);
        return;
      }
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        Swal.fire("File too large", `Maximum file size is ${MAX_FILE_MB} MB.`, "error");
        e.target.value = "";
        setFile(null);
        return;
      }
    }
    setFile(f);
  };

  const openAdd = () => {
    setModal({ mode: "add" });
    setQuestion("");
    setAnswer("");
    setFile(null);
  };

  const openEdit = (q) => {
    setModal({ mode: "edit", id: q.id, hadMedia: !!q.question_hint });
    setQuestion(he.decode(q.question_name));
    setAnswer(he.decode(q.correct_answer));
    setFile(null);
  };

  const closeModal = () => {
    setModal(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const q = question.trim();
    const a = answer.trim();

    let problem = "";
    if (!q && !file && !modal.hadMedia) problem = "Please enter a Question or upload an Image!";
    else if (q.length > MAX_QUESTION_LEN) problem = `Question must be ${MAX_QUESTION_LEN} characters or less!`;
    else if (!a) problem = "Answer cannot be empty!";
    else if (a.length > MAX_ANSWER_LEN) problem = `Answer must be ${MAX_ANSWER_LEN} characters or less!`;
    else if (!/^[a-zA-Z\s]+$/.test(a)) problem = "Answer must contain only letters!";
    if (problem) {
      Swal.fire(problem, "", "error");
      return;
    }

    const formData = new FormData();
    formData.append("themeName", currentTheme);
    formData.append("action", modal.mode);
    formData.append("edit_id", modal.id || 0);
    formData.append("question", q);
    formData.append("answer", a);
    if (file) formData.append("reference_file", file);

    setSaving(true);
    try {
      await saveQuestion({ token: adminToken, formData });
      closeModal();
      await Swal.fire({ title: "Question Saved Successfully!", icon: "success", timer: 1500, showConfirmButton: false });
      load();
    } catch (err) {
      Swal.fire(err.message, "", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (q) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this item?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });
    if (!result.isConfirmed) return;
    try {
      await deleteQuestion({ token: adminToken, id: q.id });
      load();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const downloadFile = (name, content, type) => {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    try {
      const json = await exportQuestionsCSV({ token: adminToken, themeName: currentTheme });
      downloadFile("questions.csv", json.csv, "text/csv;charset=utf-8");
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      Swal.fire("Please choose a CSV file", "", "error");
      return;
    }
    const proceed = await Swal.fire({
      title: "Replace all questions?",
      text: "Uploading a CSV replaces every question of this theme.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, upload",
    });
    if (!proceed.isConfirmed) return;

    const formData = new FormData();
    formData.append("themeName", currentTheme);
    formData.append("file", csvFile);
    setCsvBusy(true);
    try {
      const json = await importQuestionsCSV({ token: adminToken, formData });
      setCsvOpen(false);
      setCsvFile(null);
      await Swal.fire("Successfully Updated", `Imported ${json.imported} questions`, "success");
      load();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setCsvBusy(false);
    }
  };

  if (!currentTheme) {
    return (
      <div className="qe-empty">
        <p>No theme selected.</p>
        <button type="button" className="qe-btn" onClick={() => navigate("/admin")}>
          Go to themes
        </button>
      </div>
    );
  }

  return (
    <div className="qe-page">
      <div className="qe-toolbar">
        <button type="button" className="qe-btn qe-back" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <div className="qe-actions">
          <button type="button" className="qe-btn" onClick={openAdd}>
            <i className="fa-solid fa-plus" /> Add Question
          </button>
          <button type="button" className="qe-btn" onClick={() => setCsvOpen(true)}>
            <i className="fa-solid fa-upload" /> Upload CSV
          </button>
          <button type="button" className="qe-btn" onClick={handleDownload}>
            <i className="fa-solid fa-download" /> Download CSV
          </button>
          <a className="qe-btn" href="/questionsSample.csv" download>
            <i className="fa-solid fa-file-csv" /> Sample CSV
          </a>
          <button type="button" className="qe-btn" onClick={startTour}>
            <i className="fa-solid fa-circle-info" /> Take a Tour
          </button>
        </div>
      </div>

      <div className="qe-card">
        <table className="qe-table">
          <thead>
            <tr>
              <th>No</th>
              <th>Question</th>
              <th>Answer</th>
              <th>Image</th>
              <th>Edit</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6}>Loading...</td>
              </tr>
            )}
            {!loading && questions.length === 0 && (
              <tr>
                <td colSpan={6}>No questions yet. Add a question or upload a CSV.</td>
              </tr>
            )}
            {!loading &&
              visible.map((q, i) => (
                <tr key={q.id}>
                  <td>{pageStart + i + 1}</td>
                  <td className="qe-left">{he.decode(q.question_name)}</td>
                  <td>{he.decode(q.correct_answer)}</td>
                  <td>
                    <Media q={q} />
                  </td>
                  <td>
                    <button type="button" className="qe-btn qe-small" onClick={() => openEdit(q)}>
                      <i className="fa-solid fa-pen-to-square" /> Edit
                    </button>
                  </td>
                  <td>
                    <button type="button" className="qe-btn qe-small qe-danger" onClick={() => handleDelete(q)}>
                      <i className="fa-solid fa-trash" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {questions.length > PAGE_SIZE && (
        <div className="qe-pagination">
          <button type="button" className="qe-pgbtn" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>
            Prev
          </button>
          {pageItems(currentPage, totalPages).map((it, i) =>
            it === "..." ? (
              <span key={`gap${i}`} className="qe-gap">
                ...
              </span>
            ) : (
              <button
                key={it}
                type="button"
                className={`qe-pgbtn${it === currentPage ? " active" : ""}`}
                onClick={() => setPage(it)}
              >
                {it}
              </button>
            )
          )}
          <button
            type="button"
            className="qe-pgbtn"
            disabled={currentPage === totalPages}
            onClick={() => setPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}

      {modal && (
        <div className="qe-backdrop" onClick={closeModal}>
          <form className="qe-modal" onClick={(e) => e.stopPropagation()} onSubmit={handleSave}>
            <h4>{modal.mode === "edit" ? "Edit Question" : "Add Question"}</h4>

            <label htmlFor="qe-question">Question</label>
            <textarea
              id="qe-question"
              maxLength={MAX_QUESTION_LEN}
              placeholder="Question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
            <div className="qe-note">* Max length {MAX_QUESTION_LEN} characters.</div>

            <label htmlFor="qe-file">
              Upload Reference File{modal.hadMedia ? " (leave empty to keep the current one)" : ""}:
            </label>
            <input
              id="qe-file"
              ref={fileRef}
              type="file"
              accept={MEDIA_ACCEPT}
              onChange={onPickFile}
            />
            <div className="qe-note">* Allowed Format JPG, JPEG, PNG, GIF Only</div>

            <label htmlFor="qe-answer">Answer</label>
            <textarea
              id="qe-answer"
              maxLength={MAX_ANSWER_LEN}
              placeholder="Answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
            />
            <div className="qe-note">* Max length {MAX_ANSWER_LEN} characters. Letters and spaces only.</div>

            <div className="qe-modal-actions">
              <button type="button" className="qe-btn qe-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className="qe-btn" disabled={saving}>
                {saving ? "Saving..." : modal.mode === "edit" ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}

      {csvOpen && (
        <div className="qe-backdrop" onClick={() => setCsvOpen(false)}>
          <form className="qe-modal" onClick={(e) => e.stopPropagation()} onSubmit={handleImport}>
            <h4>Upload CSV</h4>
            <p className="qe-note">
              Columns: <code>question_name</code>, <code>correct_answer</code>. Existing questions of this theme are
              replaced.
            </p>
            <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0] || null)} />
            <div className="qe-modal-actions">
              <button type="button" className="qe-btn qe-secondary" onClick={() => setCsvOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="qe-btn" disabled={csvBusy}>
                {csvBusy ? "Uploading..." : "Upload"}
              </button>
            </div>
          </form>
        </div>
      )}

      <GuidedTourOverlay
        steps={TOUR_STEPS}
        tourRunning={tourRunning}
        tourStep={tourStep}
        tourRect={tourRect}
        onSkip={endTour}
        onNext={handleTourNext}
      />
    </div>
  );
}
